import styled from "styled-components";
import { screenMinWidth } from "src/theme";
import color, { primitive } from "src/design-system/tokens/color";
import { border_radius } from "src/design-system/tokens/border";
import { spacing } from "src/design-system/tokens/spacing";
import { tw } from "src/design-system/tw";
import { useHoveredIndex } from "../../../../HoveredIndexProvider";
import useSearch from "../../../../utils/hooks/useSearch";
import useTimelineTripPointAlternativeNames from "../../../../utils/hooks/useTimelineTripPointAlternativeNames";
import { TroniconTimelineAccommodationPromo } from "../../Shared/TroniconTimelineAccommodationPromo";
import { TroniconTimelineDestination } from "../Shared/TroniconTimelineDestination";
import { TroniconTimelineOrigin } from "../Shared/TroniconTimelineOrigin";
import { createGroupedTimelineCellsFromRoute } from "../cell/createTimelineCells";
import { TroniconTimelineDetail } from "./TroniconTimelineDetail";
import { TroniconTimelineJoint } from "./TroniconTimelineJoint";
import { TroniconTimelineTransfer } from "./TroniconTimelineTransfer";

type Props = {
  context: "transport" | "tripPlanner";
  routeIndex: number;
  onDetailClick?: (segmentIndex: number) => void;
};

export function TroniconRouteTimeline({
  context,
  routeIndex,
  onDetailClick,
}: Props) {
  const { setHoveredSegmentIndex } = useHoveredIndex();
  const { searchResponse, destination } = useSearch();
  const tripPointAlternatives = useTimelineTripPointAlternativeNames(
    routeIndex,
    undefined,
    true,
  );

  if (!searchResponse) {
    return null;
  }

  const groupedTimeline = createGroupedTimelineCellsFromRoute({
    response: searchResponse,
    routeIndex,
    tripPointAlternatives,
    destination,
  });

  return (
    <TimelineContainer>
      {groupedTimeline.map(
        ({ cells, origin, destination, isJoint, isInteractive }, i) => {
          return (
            <GroupedContainer $isJoint={isJoint} key={i}>
              <GroupedContent
                $isInteractable={isInteractive}
                $isJoint={isJoint}
              >
                {cells.map((cell) => {
                  const isStandalone = cells.length === 1;
                  switch (cell.type) {
                    case "origin":
                      return (
                        <TroniconTimelineOrigin
                          className={tw("m-0 p-2 pb-0", isStandalone && "pb-2")}
                          key={cell.id}
                          cell={cell}
                        />
                      );
                    case "destination":
                      return (
                        <TroniconTimelineDestination
                          className={tw("m-0 p-2 pt-0", isStandalone && "pt-2")}
                          key={cell.id}
                          cell={cell}
                          isLineJoined={false}
                        />
                      );
                    case "detail":
                      return (
                        <GroupedTroniconTimelineDetail
                          context={context}
                          key={cell.id}
                          cell={cell}
                          onClick={onDetailClick}
                          onHover={setHoveredSegmentIndex}
                          isCompact={cell.transitMode === "walk"}
                          origin={origin}
                          destination={destination}
                        />
                      );
                    case "interchange":
                      return null;
                    case "transfer":
                      return (
                        <TroniconTimelineTransfer key={cell.id} cell={cell} />
                      );
                    case "joint":
                      return <TroniconTimelineJoint key={cell.id} />;
                    case "accommodation":
                      return (
                        <GroupedTroniconTimelineAccommodationPromo
                          key={cell.id}
                          cell={cell}
                          loggingFrom="Route"
                        />
                      );
                    default:
                      throw Error(
                        `Unrecognised Cell Type: ${JSON.stringify(cell)}`,
                      );
                  }
                })}
              </GroupedContent>
            </GroupedContainer>
          );
        },
      )}
    </TimelineContainer>
  );
}

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  background-color: ${color.bg.surface.secondaryActive};
  padding: ${spacing.xxl} ${spacing.xl} ${spacing.xxxl};

  ${screenMinWidth.sm} {
    background-color: unset;
    padding: unset;
  }
`;

const GroupedContainer = styled.div<{ $isJoint?: boolean }>`
  background-color: ${(props) =>
    props.$isJoint ? "unset" : primitive.neutral_100};
  border-radius: ${border_radius.rounded_lg};
  padding: ${(props) => (props.$isJoint ? `0 ${spacing.md}` : spacing.md)};
`;

const GroupedContent = styled.div<{
  $isInteractable?: boolean;
  $isJoint?: boolean;
}>`
  position: relative;
  border-radius: ${border_radius.rounded_md};
  transition: background-color 0.05s ease-in-out;
  &:hover {
    background-color: ${(props) =>
      props.$isInteractable
        ? props.$isJoint
          ? primitive.neutral_700
          : primitive.neutral_300
        : undefined};
  }
`;

const GroupedTroniconTimelineDetail = styled(TroniconTimelineDetail)`
  margin: 0;
  padding: 0 ${spacing.md};
`;

const GroupedTroniconTimelineAccommodationPromo = styled(
  TroniconTimelineAccommodationPromo,
)`
  margin: 0;
  padding: ${spacing.md};
`;
